/*
 * ------------------------------------------------------------
 * CLIENT CUSTOMIZATION — meba
 *
 * Glint hover utility. A canvas-based "luxury light edge" that, on
 * hover, traces a thin light streak along an element's top edge, turns
 * the top-right corner, runs down the right side, and ends with a small
 * sparkle flare. Ported from the meba homepage mockup (MeUI/flare).
 *
 * Apply it by either:
 *   - adding class="glint" to any element you control, or
 *   - adding a CSS selector to GLINT_SELECTORS below (for base-theme
 *     elements we don't edit directly, e.g. the banner product card).
 *
 * Auto-initialises on load and on shopify:section:load (theme editor),
 * and is a no-op under prefers-reduced-motion.
 * ------------------------------------------------------------
 */
(function () {
  'use strict';

  // Base/section elements to glint on hover without touching their markup.
  var GLINT_SELECTORS = ['.banner__product-card', '.banner__buttons .button'];

  // Elements that glint automatically on a timer instead of on hover. Each
  // element must already establish its own positioning context (relative or
  // absolute) so the glint canvas can overlay it. { selector, interval (ms) }.
  var GLINT_AUTO = [{ selector: '.cspot__badge', interval: 7000, intensity: 2 }];

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  function buildGlint(element, options) {
    var canvas = document.createElement('canvas');
    canvas.className = 'glint-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    element.appendChild(canvas);

    var ctx = canvas.getContext('2d');
    var dpr = window.devicePixelRatio || 1;
    // intensity scales brightness (opacity + glow) and size (streak thickness
    // and flare). 1 = the original look; higher = brighter and bigger.
    var intensity = (options && options.intensity) || 1;
    var padding = 20;
    var duration = 2200;
    var animationId = null,
      startTime = null;
    var seedH = 1,
      seedV = 1,
      angleSkew = 0;

    function resize() {
      var rect = element.getBoundingClientRect();
      canvas.width = Math.max(1, rect.width + padding * 2) * dpr;
      canvas.height = Math.max(1, rect.height + padding * 2) * dpr;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    }

    function drawRay(x, y, length, thickness, angle) {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.beginPath();
      ctx.moveTo(-length, 0);
      ctx.lineTo(0, -thickness);
      ctx.lineTo(length, 0);
      ctx.lineTo(0, thickness);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    function drawSpindle(x1, x2, y, peak, opacity) {
      if (x2 <= x1) return;
      ctx.save();
      var center = x1 + (x2 - x1) * 0.5;
      var segLen = x2 - x1;
      var w = segLen * 0.38;
      var sX1 = center - w / 2,
        sX2 = center + w / 2;
      var midX = center,
        h = peak / 2;
      var g = ctx.createLinearGradient(sX1, y, sX2, y);
      g.addColorStop(0, 'rgba(255,255,255,0)');
      g.addColorStop(0.2, 'rgba(255,255,255,' + Math.min(1, opacity * 0.8 * intensity) + ')');
      g.addColorStop(0.8, 'rgba(255,255,255,' + Math.min(1, opacity * 0.8 * intensity) + ')');
      g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = g;
      ctx.shadowBlur = 1.5 * intensity;
      ctx.shadowColor = '#fff';
      ctx.beginPath();
      ctx.moveTo(sX1, y);
      ctx.bezierCurveTo(sX1 + (midX - sX1) * 0.5, y - h * 0.2, midX - (midX - sX1) * 0.3, y - h, midX, y - h);
      ctx.bezierCurveTo(midX + (sX2 - midX) * 0.3, y - h, sX2 - (sX2 - midX) * 0.5, y - h * 0.2, sX2, y);
      ctx.bezierCurveTo(sX2 - (sX2 - midX) * 0.5, y + h * 0.2, midX + (sX2 - midX) * 0.3, y + h, midX, y + h);
      ctx.bezierCurveTo(midX - (midX - sX1) * 0.3, y + h, sX1 + (midX - sX1) * 0.5, y + h * 0.2, sX1, y);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    function animate(ts) {
      if (!startTime) startTime = ts;
      var elapsed = ts - startTime;
      var progress = Math.min(elapsed / duration, 1);
      var W = canvas.width / dpr,
        H = canvas.height / dpr;
      var elemW = W - padding * 2,
        elemH = H - padding * 2;
      ctx.clearRect(0, 0, W, H);

      var startX = padding + elemW * 0.15;
      var cornerX = padding + elemW;
      var cornerY = padding;
      var startY = padding + elemH * 0.35;

      var topHeadX, topTailX, sideHeadY, sideTailY;
      var opacity = 0,
        flareSize = 0,
        spThick = 0,
        spOp = 0;

      if (progress <= 0.88) {
        var lp = progress / 0.88;
        var ease = (1 - Math.cos(lp * Math.PI)) * 0.5;
        topHeadX = startX + (cornerX - startX) * ease;
        topTailX = startX + (cornerX - startX) * Math.pow(lp, 1.6) * ease;
        if (topTailX > topHeadX) topTailX = topHeadX;
        if (lp > 0.3 && lp < 0.7) {
          var iw = (lp - 0.3) / 0.4;
          spOp = Math.sin(iw * Math.PI);
          spThick = (0.5 + spOp * 0.35) * intensity;
        }
        if (lp >= 0.75) {
          var sp = (lp - 0.75) / 0.25;
          var se = Math.pow(sp, 2);
          sideHeadY = startY - (startY - cornerY) * se;
          sideTailY = startY - (startY - cornerY) * Math.pow(sp, 3);
        } else {
          sideHeadY = startY;
          sideTailY = startY;
        }
        opacity = Math.sin(lp * Math.PI * 0.5);
      } else {
        var lp2 = (progress - 0.88) / 0.12;
        topHeadX = cornerX;
        topTailX = cornerX;
        sideHeadY = cornerY;
        sideTailY = cornerY;
        opacity = 1 - lp2;
        flareSize = Math.sin(Math.pow(1 - lp2, 3) * Math.PI) * 6.5 * intensity;
      }

      if (progress < 1) {
        ctx.lineWidth = 0.5 * intensity;
        if (topHeadX > topTailX) {
          ctx.save();
          var gt = ctx.createLinearGradient(topTailX, cornerY, topHeadX, cornerY);
          gt.addColorStop(0, 'rgba(255,255,255,0)');
          gt.addColorStop(0.25, 'rgba(255,255,255,' + Math.min(1, opacity * 0.75 * intensity) + ')');
          gt.addColorStop(0.75, 'rgba(255,255,255,' + Math.min(1, opacity * 0.75 * intensity) + ')');
          gt.addColorStop(1, 'rgba(255,255,255,0)');
          ctx.beginPath();
          ctx.strokeStyle = gt;
          ctx.moveTo(topTailX, cornerY);
          ctx.lineTo(topHeadX, cornerY);
          ctx.stroke();
          ctx.restore();
        }
        if (spOp > 0 && progress <= 0.88) {
          drawSpindle(topTailX, topHeadX, cornerY, spThick, opacity * spOp);
        }
        if (progress <= 0.88 && progress / 0.88 >= 0.75 && sideTailY > sideHeadY) {
          ctx.save();
          var gs = ctx.createLinearGradient(cornerX, sideTailY, cornerX, sideHeadY);
          gs.addColorStop(0, 'rgba(255,255,255,0)');
          gs.addColorStop(0.3, 'rgba(255,255,255,' + Math.min(1, opacity * 0.7 * intensity) + ')');
          gs.addColorStop(0.7, 'rgba(255,255,255,' + Math.min(1, opacity * 0.7 * intensity) + ')');
          gs.addColorStop(1, 'rgba(255,255,255,0)');
          ctx.beginPath();
          ctx.strokeStyle = gs;
          ctx.moveTo(cornerX, sideTailY);
          ctx.lineTo(cornerX, sideHeadY);
          ctx.stroke();
          ctx.restore();
        }
        if (flareSize > 0) {
          ctx.shadowBlur = 4 * intensity;
          ctx.shadowColor = 'rgba(255,248,230,0.85)';
          ctx.fillStyle = 'rgba(255,255,255,' + Math.min(1, opacity * intensity) + ')';
          drawRay(cornerX, cornerY, flareSize * 1.2 * seedH, 0.5 * intensity, 0 + angleSkew);
          drawRay(cornerX, cornerY, flareSize * 0.7 * seedV, 0.5 * intensity, Math.PI / 2 + angleSkew);
          ctx.beginPath();
          ctx.arc(cornerX, cornerY, 0.6 * intensity, 0, Math.PI * 2);
          ctx.fill();
        }
        animationId = requestAnimationFrame(animate);
      } else {
        ctx.clearRect(0, 0, W, H);
        animationId = null;
      }
    }

    function trigger() {
      if (animationId) cancelAnimationFrame(animationId);
      startTime = null;
      seedH = 0.7 + Math.random() * 0.6;
      seedV = 0.6 + Math.random() * 0.5;
      angleSkew = (Math.random() - 0.5) * 0.08;
      resize();
      animationId = requestAnimationFrame(animate);
    }

    function abort() {
      if (animationId) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    if (options && options.interval) {
      // Auto mode: replay on a fixed interval, no hover needed.
      setInterval(trigger, options.interval);
    } else {
      element.addEventListener('mouseenter', trigger);
      element.addEventListener('mouseleave', abort);
    }
  }

  function initGlint(element, options) {
    if (!element || element.dataset.glint) return;
    element.dataset.glint = '1';
    var opts = {
      interval: options && options.interval,
      intensity: (options && options.intensity) || 1
    };
    // Per-element override, e.g. <a class="glint" data-glint-intensity="1.6">.
    if (element.dataset.glintIntensity) {
      opts.intensity = parseFloat(element.dataset.glintIntensity) || opts.intensity;
    }
    // Auto elements keep their own positioning; only hover elements get the
    // .glint helper class (which forces position: relative).
    if (!opts.interval) element.classList.add('glint');
    buildGlint(element, opts);
  }

  function scan(root) {
    root = root || document;
    var selector = ['.glint'].concat(GLINT_SELECTORS).join(', ');
    root.querySelectorAll(selector).forEach(function (el) { initGlint(el); });
    // querySelectorAll only matches descendants — also init the root
    // itself when a reloaded section element matches.
    if (root.matches && root.matches(selector)) initGlint(root);

    // Auto-glint (timer-driven) elements.
    GLINT_AUTO.forEach(function (cfg) {
      var opts = { interval: cfg.interval, intensity: cfg.intensity };
      root.querySelectorAll(cfg.selector).forEach(function (el) { initGlint(el, opts); });
      if (root.matches && root.matches(cfg.selector)) initGlint(root, opts);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    });
  } else {
    scan(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });

  // ----------------------------------------------------------------
  // Newsletter (meba): hovering the Klaviyo submit button sweeps a glint
  // light-streak along the Phone field's bottom border line. The Phone field
  // is the row with the screen-reader-only label; the streak canvas is hosted
  // on its (position:relative) wrapper. Lazily wired on first hover, since the
  // Klaviyo form renders after load.
  // ----------------------------------------------------------------
  function buildLineGlint(button) {
    var block = button.closest('.footer-newsletter');
    if (!block) return null;

    var phoneWrap = null;
    block.querySelectorAll('[data-testid="form-row"]').forEach(function (row) {
      if (row.querySelector('label.klaviyo-sr-only')) {
        phoneWrap = row.querySelector('.klaviyo-textinput');
      }
    });
    if (!phoneWrap) return null;

    var canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText =
      'position:absolute;left:0;right:0;bottom:-12px;width:100%;height:24px;pointer-events:none;z-index:6;';
    phoneWrap.appendChild(canvas);

    var ctx = canvas.getContext('2d');
    var dpr = window.devicePixelRatio || 1;
    var duration = 900;
    var raf = null,
      startTs = null;

    function resize() {
      var rect = phoneWrap.getBoundingClientRect();
      canvas.width = Math.max(1, rect.width) * dpr;
      canvas.height = 24 * dpr;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    }

    function frame(ts) {
      if (!startTs) startTs = ts;
      var p = Math.min((ts - startTs) / duration, 1);
      var W = canvas.width / dpr;
      var H = canvas.height / dpr;
      var y = H / 2;
      ctx.clearRect(0, 0, W, H);

      var ease = (1 - Math.cos(p * Math.PI)) * 0.5;
      var seg = W * 0.4;
      var head = -seg + (W + seg * 2) * ease;
      var tail = head - seg;
      var opacity = Math.sin(p * Math.PI);

      ctx.save();
      var g = ctx.createLinearGradient(tail, y, head, y);
      g.addColorStop(0, 'rgba(255,255,255,0)');
      g.addColorStop(0.5, 'rgba(255,255,255,' + opacity * 0.9 + ')');
      g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.lineWidth = 1;
      ctx.strokeStyle = g;
      ctx.shadowBlur = 6;
      ctx.shadowColor = 'rgba(255,248,230,0.8)';
      ctx.beginPath();
      ctx.moveTo(tail, y);
      ctx.lineTo(head, y);
      ctx.stroke();
      // Leading sparkle.
      ctx.fillStyle = 'rgba(255,255,255,' + opacity + ')';
      ctx.beginPath();
      ctx.arc(head, y, 1.1, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      if (p < 1) {
        raf = requestAnimationFrame(frame);
      } else {
        ctx.clearRect(0, 0, W, H);
        raf = null;
      }
    }

    function start() {
      if (raf) cancelAnimationFrame(raf);
      startTs = null;
      resize();
      raf = requestAnimationFrame(frame);
    }

    function stop() {
      if (raf) {
        cancelAnimationFrame(raf);
        raf = null;
      }
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    button.addEventListener('mouseenter', start);
    button.addEventListener('mouseleave', stop);
    return { start: start };
  }

  document.addEventListener('mouseover', function (event) {
    if (!event.target.closest) return;
    var button = event.target.closest('.footer-newsletter .klaviyo-form-button');
    if (!button || button.dataset.lineGlint) return;
    var glint = buildLineGlint(button);
    if (glint) {
      button.dataset.lineGlint = '1';
      glint.start(); // we're already hovering, so play immediately
    }
  });
})();
